import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { chatApi, uploadAttachment } from "../api/chat";
import type {
  Attachment,
  AttachmentKind,
  ChatConversation,
  ChatConversationDetail,
  ChatMediaPage,
  ChatMessage,
  ChatMessagePage,
  ChatPreferences,
  ChatSearchParams,
  CreateConversationPayload,
  UploadAttachmentOptions,
} from "../api/chat";
import { requireData, yieldToCancellation } from "./http";

export const chatQueryKeys = {
  all: ["chat"] as const,
  conversations: ["chat", "conversations"] as const,
  conversation: (id: string) => ["chat", "conversation", id] as const,
  projectConversation: (projectId: string) =>
    ["chat", "project-conversation", projectId] as const,
  messages: (id: string) => ["chat", "messages", id] as const,
  unread: ["chat", "unread"] as const,
  preferences: ["chat", "preferences"] as const,
  search: (params: Omit<ChatSearchParams, "cursor">) =>
    ["chat", "search", params] as const,
  /**
   * Omitting `kind` yields a prefix of every kind-filtered key below it, so
   * this same builder both keys the query and, called without a kind,
   * invalidates every filtered variant at once.
   */
  media: (id: string, kind?: AttachmentKind) =>
    kind
      ? (["chat", "media", id, kind] as const)
      : (["chat", "media", id] as const),
};

export const conversationsQueryOptions = () =>
  queryOptions({
    queryKey: chatQueryKeys.conversations,
    queryFn: async ({ signal }): Promise<ChatConversation[]> => {
      await yieldToCancellation(signal);
      return requireData(await chatApi.listConversations(signal)).conversations;
    },
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });

export const useConversations = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({ ...conversationsQueryOptions(), enabled });

export const useConversation = (id: string | null) =>
  useQuery({
    queryKey: chatQueryKeys.conversation(id || "none"),
    queryFn: async ({ signal }): Promise<ChatConversationDetail> => {
      await yieldToCancellation(signal);
      return requireData(await chatApi.getConversation(id as string, signal));
    },
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });

/**
 * The project's own conversation.
 *
 * A 404 is the ordinary answer for a project whose conversation the caller is
 * not in, so it is not retried and the caller renders nothing rather than an
 * error.
 */
export const useProjectConversation = (
  projectId: string | null,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: chatQueryKeys.projectConversation(projectId || "none"),
    queryFn: async ({ signal }): Promise<ChatConversationDetail | null> => {
      await yieldToCancellation(signal);
      const { data, error } = await chatApi.getProjectConversation(
        projectId as string,
        signal,
      );
      if (error) {
        if (error.status === 404) return null;
        throw error;
      }
      return data ?? null;
    },
    enabled: enabled && Boolean(projectId),
    retry: false,
    staleTime: 60 * 1000,
  });

/** A thread's history as cached: one API page per entry, newest page first. */
export type MessagePages = InfiniteData<ChatMessagePage, string | undefined>;

/**
 * Flattens cached pages into one newest-first list.
 *
 * A page fetched with a fixed cursor overlaps the page above it once newer
 * messages have pushed the boundary down, so ids are deduplicated here rather
 * than trusted to be unique across pages.
 */
export const flattenMessagePages = (pages: ChatMessagePage[]): ChatMessage[] => {
  const seen = new Set<string>();
  const messages: ChatMessage[] = [];
  for (const page of pages) {
    for (const message of page.messages) {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
      messages.push(message);
    }
  }
  return messages;
};

/**
 * A thread's history, one page at a time. The first page is the newest;
 * `fetchNextPage` loads the page before the oldest message held so far.
 */
export const useMessages = (id: string | null) =>
  useInfiniteQuery({
    queryKey: chatQueryKeys.messages(id || "none"),
    queryFn: async ({ signal, pageParam }): Promise<ChatMessagePage> => {
      await yieldToCancellation(signal);
      return requireData(
        await chatApi.listMessages(
          id as string,
          pageParam ? { before: pageParam } : {},
          signal,
        ),
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_before ?? undefined,
    select: (data) => flattenMessagePages(data.pages),
    enabled: Boolean(id),
    // Live frames keep this fresh, so a refocus refetch is wasted work.
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

/**
 * A conversation's attachments for the media sidebar, optionally by kind,
 * newest first. `fetchNextPage` loads the page before the oldest one held.
 */
export const useConversationMedia = (
  conversationId: string | null,
  kind?: AttachmentKind,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useInfiniteQuery({
    queryKey: chatQueryKeys.media(conversationId || "none", kind),
    queryFn: async ({ signal, pageParam }): Promise<ChatMediaPage> => {
      await yieldToCancellation(signal);
      return requireData(
        await chatApi.listMedia(
          conversationId as string,
          pageParam ? { kind, before: pageParam } : { kind },
          signal,
        ),
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_before ?? undefined,
    select: (data) => data.pages.flatMap((page) => page.attachments),
    enabled: enabled && Boolean(conversationId),
    staleTime: 60 * 1000,
  });

/**
 * Search results, best match first, a page at a time. Disabled until the
 * caller has something to search for, since the API refuses an empty search.
 */
export const useMessageSearch = (
  params: Omit<ChatSearchParams, "cursor">,
  enabled: boolean,
) =>
  useInfiniteQuery({
    queryKey: chatQueryKeys.search(params),
    queryFn: async ({ signal, pageParam }) => {
      await yieldToCancellation(signal);
      return requireData(
        await chatApi.search(
          pageParam ? { ...params, cursor: pageParam } : params,
          signal,
        ),
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    select: (data) => data.pages.flatMap((page) => page.messages),
    enabled,
    staleTime: 30 * 1000,
  });

export const useChatPreferences = () =>
  useQuery({
    queryKey: chatQueryKeys.preferences,
    queryFn: async ({ signal }) => {
      await yieldToCancellation(signal);
      return requireData(await chatApi.preferences(signal));
    },
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateChatPreferences = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (preferences: ChatPreferences) =>
      requireData(await chatApi.updatePreferences(preferences)),
    onSuccess: (preferences) =>
      client.setQueryData(chatQueryKeys.preferences, preferences),
  });
};

export const useUnreadCount = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: chatQueryKeys.unread,
    queryFn: async ({ signal }): Promise<number> => {
      await yieldToCancellation(signal);
      return requireData(await chatApi.unreadCount(signal)).unread_count;
    },
    enabled,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });

/**
 * Inserts a message into a cached thread, ignoring one that is already there.
 *
 * A sender sees its own message twice, once as the HTTP response and once as a
 * socket frame, so identity is deduplicated on message id rather than assumed
 * from arrival order.
 */
export const mergeMessage = (
  client: QueryClient,
  conversationId: string,
  message: ChatMessage,
) => {
  client.setQueryData<MessagePages>(
    chatQueryKeys.messages(conversationId),
    (current) => {
      if (!current?.pages.length) return current;
      if (
        current.pages.some((page) =>
          page.messages.some((existing) => existing.id === message.id),
        )
      ) {
        return current;
      }
      // History is newest first, so a new message heads the newest page.
      const [newest, ...older] = current.pages;
      return {
        ...current,
        pages: [{ ...newest, messages: [message, ...newest.messages] }, ...older],
      };
    },
  );
};

/** Marks a cached message as withdrawn without refetching the whole thread. */
export const markMessageDeleted = (
  client: QueryClient,
  conversationId: string,
  messageId: string,
) => {
  client.setQueryData<MessagePages>(
    chatQueryKeys.messages(conversationId),
    (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              messages: page.messages.map((message) =>
                message.id === messageId
                  ? {
                      ...message,
                      body: null,
                      deleted_at: message.deleted_at || new Date().toISOString(),
                    }
                  : message,
              ),
            })),
          }
        : current,
  );
  // Withdrawing a message withdraws its media, so the shared files sheet
  // drops it too, whichever path the withdrawal arrived by.
  void client.invalidateQueries({ queryKey: chatQueryKeys.media(conversationId) });
};

export const invalidateConversations = (client: QueryClient) =>
  Promise.all([
    client.invalidateQueries({ queryKey: chatQueryKeys.conversations }),
    client.invalidateQueries({ queryKey: chatQueryKeys.unread }),
  ]);

/** A plain string is a body-only send; the rest of the surface takes attachments too. */
export type SendMessageInput = string | { body?: string; attachment_ids?: string[] };

export const useSendMessage = (conversationId: string | null) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendMessageInput) => {
      const payload = typeof input === "string" ? { body: input } : input;
      return requireData(
        await chatApi.sendWithAttachments(conversationId as string, payload),
      );
    },
    onSuccess: (message) => {
      if (!conversationId) return;
      mergeMessage(client, conversationId, message);
      void client.invalidateQueries({ queryKey: chatQueryKeys.conversations });
      void client.invalidateQueries({
        queryKey: chatQueryKeys.media(conversationId),
      });
    },
  });
};

/**
 * Uploads one attachment ahead of sending. `conversationId` is bound at the
 * hook the same way `useSendMessage`'s is, since a composer only ever uploads
 * into the thread it is already open on.
 */
export const useUploadAttachment = (conversationId: string | null) =>
  useMutation({
    mutationFn: async (
      variables: { file: File } & UploadAttachmentOptions,
    ): Promise<Attachment> => {
      const { file, ...options } = variables;
      return requireData(
        await uploadAttachment(conversationId as string, file, options),
      );
    },
  });

export const useDeleteMessage = (conversationId: string | null) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) =>
      requireData(await chatApi.deleteMessage(messageId)),
    onSuccess: (message) => {
      if (!conversationId) return;
      markMessageDeleted(client, conversationId, message.id);
      void client.invalidateQueries({ queryKey: chatQueryKeys.conversations });
    },
  });
};

export const useCreateConversation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateConversationPayload) =>
      requireData(await chatApi.create(payload)),
    onSuccess: () => invalidateConversations(client),
  });
};

export const useRenameConversation = (conversationId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (title: string) =>
      requireData(await chatApi.rename(conversationId, title)),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: chatQueryKeys.conversation(conversationId),
      });
      return invalidateConversations(client);
    },
  });
};

/**
 * Adds or removes one member of a group. Both end in the same refresh as a
 * rename, because the inbox header and the list read participants from the
 * conversation list.
 */
const useParticipantMutation = (
  conversationId: string,
  change: typeof chatApi.addParticipant,
) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      requireData(await change(conversationId, userId)),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: chatQueryKeys.conversation(conversationId),
      });
      return invalidateConversations(client);
    },
  });
};

export const useAddParticipant = (conversationId: string) =>
  useParticipantMutation(conversationId, chatApi.addParticipant);

export const useRemoveParticipant = (conversationId: string) =>
  useParticipantMutation(conversationId, chatApi.removeParticipant);

export const useMarkRead = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) =>
      requireData(await chatApi.markRead(conversationId)),
    onSuccess: () => invalidateConversations(client),
  });
};
